// One row of the to-do list. Compact on desktop (single horizontal
// row with chips), reflows on mobile to drop the chips down into the
// expanded detail panel. Click anywhere outside the checkbox/trash to
// expand.

import { Icon } from "../../icons";
import type { TodoItem, TodoTag } from "../../lib/rpc";
import { QueuedDot } from "../../components/queued-dot";
import { formatDueAt, isOverdue, isSoon } from "./helpers";
import { TodoDetail } from "./todo-detail";

interface TodoRowProps {
  todo: TodoItem;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  onExpand: () => void;
  onUpdate: (patch: Partial<TodoItem>) => void;
  tagOptions: TodoTag[];
}

export function TodoRow({ todo, expanded, onToggle, onRemove, onExpand, onUpdate, tagOptions }: TodoRowProps) {
  const due = formatDueAt(todo.dueAt);
  const overdue = isOverdue(todo.dueAt) && !todo.done;
  const soon = isSoon(todo.dueAt) && !todo.done && !overdue;
  const tag = todo.tags?.[0];

  return (
    <div className="todo-row" data-done={todo.done} data-expanded={expanded}>
      <span
        className="todo-cbx"
        data-checked={todo.done}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        role="checkbox"
        aria-checked={todo.done}
      >
        <Icon name="check" size={12} />
      </span>

      {todo.priority === "high"
        ? <span className="todo-flag" title="Important"><Icon name="star-fill" size={11} /></span>
        : <span style={{ width: 11 }} aria-hidden />}

      <span className="todo-text" onClick={onExpand}>
        {todo.title}
        <QueuedDot queued={todo._queued} />
      </span>

      {todo.alarmEnabled && todo.dueAt && !todo.done && (
        <span className="todo-bell-mob" onClick={onExpand} title={due ? `Reminder at ${due}` : "Reminder set"}>
          <Icon name="alarm" size={13} />
        </span>
      )}

      {due && (
        <span
          className="todo-due"
          data-overdue={overdue}
          data-soon={soon}
          onClick={onExpand}
        >
          {todo.alarmEnabled && <Icon name="alarm" size={10} />}
          {due}
        </span>
      )}

      {todo.rrule && !todo.done && (
        <span
          className="todo-recur-mark"
          title="Recurring — next occurrence spawns when you check it off"
          onClick={onExpand}
        >
          ↻
        </span>
      )}

      {tag && (
        <span className="todo-tag" onClick={onExpand}>#{tag}</span>
      )}

      {todo.body && (
        <span className="todo-notes-marker" title="has notes" onClick={onExpand}>
          <Icon name="notes" size={12} />
        </span>
      )}

      <button className="todo-trash" onClick={onRemove} title="Delete">
        <Icon name="x" size={14} />
      </button>

      {expanded && <TodoDetail todo={todo} onUpdate={onUpdate} tagOptions={tagOptions} />}
    </div>
  );
}
